import { usePathname } from 'expo-router';
import useStore from '@/stores/useStore';
import { ROUTES } from '@/shared/constants/routes';
import { green, sand, offWhite } from '@/shared/styles/colors';
import useRouteThemeStore from '@/stores/useRouteThemeStore';
import { RouteTheme } from '../styles/theme';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

const defaultTheme: RouteTheme = {
  statusBarStyle: 'dark-content',
  topSafeAreaBackground: sand,
  bottomSafeAreaBackground: green,
};

const fullGreenTheme: RouteTheme = {
  statusBarStyle: 'light-content',
  topSafeAreaBackground: green,
  bottomSafeAreaBackground: green,
};

// Pre-define theme objects to prevent recreation
const clientPreviewTheme: RouteTheme = {
  statusBarStyle: 'dark-content',
  topSafeAreaBackground: sand,
  bottomSafeAreaBackground: green,
};

const clientInstantStylingTheme: RouteTheme = {
  statusBarStyle: 'light-content',
  topSafeAreaBackground: green,
  bottomSafeAreaBackground: sand,
};

const onboardingTheme: RouteTheme = {
  statusBarStyle: 'dark-content',
  topSafeAreaBackground: sand,
  bottomSafeAreaBackground: sand,
};

const clientOnboardingTheme: RouteTheme = {
  statusBarStyle: 'dark-content',
  topSafeAreaBackground: sand,
  bottomSafeAreaBackground: offWhite,
};

export const useRouteTheme = (): RouteTheme => {
  const pathname = usePathname();
  const isStylist = useStore((s) => s.isStylist);
  const { override, clearOverride } = useRouteThemeStore(
    useShallow((state) => ({
      override: state.override,
      clearOverride: state.clearOverride,
    })),
  );

  // Automatically clear override when route changes
  useEffect(() => {
    clearOverride();
  }, [pathname, isStylist, clearOverride]);

  const routeTheme = useMemo(
    () => getThemeForRoute(pathname, Boolean(isStylist)),
    [pathname, isStylist],
  );

  // Memoize the final theme to prevent object recreation
  return useMemo(() => {
    if (!override) return routeTheme;

    // Only create new object if override exists
    return {
      statusBarStyle: override.statusBarStyle ?? routeTheme.statusBarStyle,
      topSafeAreaBackground:
        override.topSafeAreaBackground ?? routeTheme.topSafeAreaBackground,
      bottomSafeAreaBackground:
        override.bottomSafeAreaBackground ??
        routeTheme.bottomSafeAreaBackground,
    };
  }, [routeTheme, override]);
};

const getThemeForRoute = (route: string, isStylist: boolean): RouteTheme => {
  switch (route) {
    case ROUTES.PREVIEW.path:
      return isStylist ? defaultTheme : clientPreviewTheme;

    case ROUTES.INSTANT_STYLING.path:
      return isStylist ? defaultTheme : clientInstantStylingTheme;

    case ROUTES.WELCOME.path:
    case ROUTES.WELCOME_INTRO('stylist').path:
    case ROUTES.WELCOME_INTRO('client').path:
    case ROUTES.PRIVACY.path:
    case ROUTES.TERMS.path:
    case ROUTES.FAQ.path:
    case ROUTES.VIDEO_SESSION.path:
      return fullGreenTheme;

    case ROUTES.ONBOARDING.path:
      return onboardingTheme;

    case ROUTES.CLIENT_ONBOARDING.path:
      return clientOnboardingTheme;

    default:
      return defaultTheme;
  }
};
